import _ from 'lodash';
import { TYPE_BEAT_ENUM } from '@/shared/config';
import { findInBulkChanges } from '@/entities/beat';

/**
 * Reports whether a beat type is one that carries morphology templates in the beats tab. Returns true only for the S and V types.
 *
 * @param activeButton - Identifier of the active beat type button.
 * @returns True when the active button is S or V.
 */
export const hasTemplateBeat = (activeButton: any) => ['S', 'V'].includes(activeButton);

export const handleUpdateBeatReview = ({
  beatChanges = [] as any[],
  strips = [] as any[],
  isReviewed,
  isReset = false,
  bulkReviewedBeats = [] as any[],
  resetBulkReviewedBeats = [] as any[],
  activeButton,
}: {
  beatChanges?: any[];
  strips?: any[];
  isReviewed: any;
  isReset?: boolean;
  bulkReviewedBeats?: any[];
  resetBulkReviewedBeats?: any[];
  activeButton: any;
}) => {
  const cloneBeatChanges: any[] = _.cloneDeep(beatChanges);
  const bulkReviewResetBeats: any[] = _.cloneDeep(resetBulkReviewedBeats || []);
  _.forEach(strips, (strip: any) => {
    const index = _.findIndex(cloneBeatChanges, (x: any) => x.id === strip.id);
    const inBulkReviewed = findInBulkChanges(bulkReviewedBeats, strip);
    if (isReset || strip?.originalReviewed === isReviewed) {
      if (index > -1) {
        _.remove(cloneBeatChanges, (x: any) => x.id === strip.id);
      }
      if (inBulkReviewed) {
        bulkReviewResetBeats.push({
          id: strip.id,
          cellId: strip.cellId,
          originalReviewed: strip?.originalReviewed,
          type: activeButton,
          originalTemplateId: strip?.templateId || null,
        });
      }
    } else if (inBulkReviewed && inBulkReviewed.isReviewed === isReviewed) {
      if (index > -1) {
        _.remove(cloneBeatChanges, (x: any) => x.id === strip.id);
      }
      _.remove(bulkReviewResetBeats, (x: any) => x.id === strip.id);
    } else if (index > -1) {
      cloneBeatChanges[index].isReviewed = isReviewed;
      _.remove(bulkReviewResetBeats, (x: any) => x.id === strip.id);
    } else {
      cloneBeatChanges.push({
        ...strip,
        isReviewed,
        beatType: activeButton,
      });
      _.remove(bulkReviewResetBeats, (x: any) => x.id === strip.id);
    }
  });
  return {
    beatReviewChanges: _.uniqBy(cloneBeatChanges, 'id'),
    bulkReviewResetBeats: _.uniqBy(bulkReviewResetBeats, 'id'),
  };
};

/**
 * Computes the percentage of beats reviewed for one beat type in the RR heat map, along with whether pending edits changed that figure. Starts from the stored total and reviewed counts for the type, then adjusts the reviewed tally for each matching bulk review entry, each reset entry and each individual beat change before clamping the percentage into the 0 to 100 band with 99.9 and 0.09 as the near-boundary values.
 *
 * @param info - Object carrying beatChanges, bulkReviewedBeats, resetBulkReviewedBeats, rrOverviewOrigin and the beat type to measure.
 * @returns An object carrying reviewedProgress as a clamped percentage and isChangeReviewd flagging pending review edits.
 */
export const calculateBeatReviewProgress = (info: any = {}) => {
  const {
    beatChanges = [] as any[],
    bulkReviewedBeats = [] as any[],
    resetBulkReviewedBeats,
    rrOverviewOrigin,
    type,
  } = info || {};
  const currentOverview = rrOverviewOrigin ? rrOverviewOrigin[type] : {};
  const totalBeats = currentOverview.total || 0;
  let reviewedBeats = currentOverview.reviewed || 0;
  let isChangeReviewd = false;
  const filterBeatChanges = _.filter(beatChanges, (x: any) => x.beatType === type);
  if (_.isEmpty(bulkReviewedBeats) && _.isEmpty(filterBeatChanges)) {
    const percent = totalBeats === 0 ? 0 : (reviewedBeats / totalBeats) * 100;
    return {
      reviewedProgress:
        percent > 99.9 && percent < 100
          ? 99.9
          : percent <= 0
            ? 0
            : percent < 0.1
              ? 0.09
              : percent > 100
                ? 100
                : percent.toFixed(1),
      isChangeReviewd: false,
    };
  }
  if (!_.isEmpty(bulkReviewedBeats)) {
    _.forEach(bulkReviewedBeats, (bulk: any) => {
      if (bulk.type === type) {
        isChangeReviewd = true;
        if (bulk.isReviewed) {
          reviewedBeats += bulk.totalCount - bulk.originalReviewedCount;
        } else {
          reviewedBeats -= bulk.originalReviewedCount;
        }
      }
    });

    const filterReset = _.filter(resetBulkReviewedBeats, (x: any) => x.type === type);
    _.forEach(filterReset, (reset: any) => {
      reviewedBeats += reset.originalReviewed ? 1 : -1;
    });
  }

  _.forEach(filterBeatChanges, (change: any) => {
    if (change.beatType === type) {
      isChangeReviewd = true;
      const isChangedReviewed = change?.originalReviewed !== change?.isReviewed;
      if (isChangedReviewed) {
        reviewedBeats += change.isReviewed ? 1 : -1;
      }
    }
  });
  const percent = totalBeats === 0 || reviewedBeats === 0 ? 0 : (reviewedBeats / totalBeats) * 100;
  return {
    reviewedProgress:
      percent > 99.9 && percent < 100
        ? 99.9
        : percent <= 0
          ? 0
          : percent < 0.1
            ? 0.09
            : percent > 100
              ? 100
              : percent.toFixed(1),
    isChangeReviewd,
  };
};

/**
 * Resolves whether a single heat map cell counts as fully reviewed and how many of its beats are reviewed. Starts from the cell's original reviewed count, applies bulk review entries and reset entries matching the cell id, applies individual beat review flips, then clamps the tally between zero and the cell's total.
 *
 * @param info - Object carrying the cell item, bulkReviewedBeats, resetBulkReviewedBeats and the beat changes already filtered to that cell.
 * @returns An object carrying isReviewed and the clamped newReviewedCount.
 */
export const calculateReviewedStatus = (info: any) => {
  const {
    resetBulkReviewedBeats = [] as any[],
    item = {} as any,
    filterBeatChanges,
    bulkReviewedBeats,
  } = info || {};
  const { originalReviewedCount, value: totalBeats, id } = item;
  let newReviewedCount = originalReviewedCount;

  if (!_.isEmpty(bulkReviewedBeats)) {
    _.forEach(bulkReviewedBeats, (bulk: any) => {
      if (bulk.cellId === id) {
        if (bulk.isReviewed) {
          newReviewedCount += bulk.totalCount - bulk.originalReviewedCount;
        } else {
          newReviewedCount -= bulk.originalReviewedCount;
        }
      }
    });
    _.forEach(resetBulkReviewedBeats, (beat: any) => {
      if (beat.cellId === id) {
        newReviewedCount += beat.originalReviewed ? 1 : -1;
      }
    });
  }
  _.forEach(filterBeatChanges, (change: any) => {
    if (change.cellId === id) {
      const isChangedReviewed = change?.originalReviewed !== change?.isReviewed;
      if (isChangedReviewed) {
        newReviewedCount += change.isReviewed ? 1 : -1;
      }
    }
  });
  return {
    isReviewed: newReviewedCount >= totalBeats,
    newReviewedCount:
      newReviewedCount < 0 ? 0 : newReviewedCount > totalBeats ? totalBeats : newReviewedCount,
  };
};

export const handleResetAppliedBuldBeats = ({
  appliedBuldBeats = [] as any[],
  strips = [] as any[],
  type,
  bulkBeatsChanges,
  isSwapTemplate,
}: {
  appliedBuldBeats?: any[];
  strips?: any[];
  type: any;
  bulkBeatsChanges: any;
  isSwapTemplate?: any;
}) => {
  const newAppliedBuldBeats: any[] = _.cloneDeep(appliedBuldBeats);
  _.forEach(strips, (strip: any) => {
    const index = _.findIndex(newAppliedBuldBeats, (x: any) => x.id === strip.id);
    const isInBuldCells = _.find(bulkBeatsChanges, (x: any) => x.cellId === strip.cellId);
    const isReset =
      (!type || strip?.originalType === type || strip?.oldType === type) && isInBuldCells;
    if (isReset) {
      newAppliedBuldBeats.push(strip);
    } else if (index > -1) {
      newAppliedBuldBeats.splice(index, 1);
    }
  });
  return _.uniqBy(newAppliedBuldBeats, 'id');
};

export const TEMPLATE_GAIN_RATIO = {
  BIG_SIZE: (334 / 146) * (5 / 0.5) * (146 / 83.39),
  SMALL_SIZE: (334 / 98) * (5 / 0.5) * (98 / 83.39),
};

export const findChangeSwap = ({
  changes,
  resetBulkChange,
  bulkChanges,
  beat,
}: {
  changes: any[];
  resetBulkChange: any[];
  bulkChanges: any[];
  beat: any;
}) => {
  const cloneBeats = { ...beat };
  const beatChange = _.find(changes, (x: any) => x.id === beat?.id);
  if (beatChange) {
    _.assign(cloneBeats, beatChange);
    return cloneBeats;
  }
  const bulkBeatChanges = findInBulkChanges(bulkChanges, beat);
  if (bulkBeatChanges && bulkBeatChanges?.templateId !== beat?.templateId) {
    const findReset = _.find(resetBulkChange, (x: any) => x.id === beat?.id);
    if (findReset) return { ...beat };
    return { ...beat, templateId: bulkBeatChanges?.templateId };
  }
  return { ...beat };
};

export const findChangeBeat = ({
  changes,
  resetBulkChange,
  bulkChanges,
  beat,
}: {
  changes: any[];
  resetBulkChange: any[];
  bulkChanges: any[];
  beat: any;
}) => {
  const cloneBeats = { ...beat };
  const beatChange = _.find(changes, (x: any) => x.id === beat?.id);
  if (beatChange) {
    _.assign(cloneBeats, beatChange);
    return cloneBeats;
  }
  const bulkBeatChanges = findInBulkChanges(bulkChanges, beat);
  if (bulkBeatChanges && bulkBeatChanges?.type !== beat?.type) {
    const findReset = _.find(resetBulkChange, (x: any) => x.id === beat?.id);
    if (findReset) return { ...beat };
    return { ...beat, type: bulkBeatChanges?.type };
  }
  return { ...beat };
};

export const findChangeReviewed = ({
  changes,
  resetBulkChange,
  bulkChanges,
  beat,
}: {
  changes: any[];
  resetBulkChange: any[];
  bulkChanges: any[];
  beat: any;
}) => {
  const cloneBeats = { ...beat };
  const beatChange = _.find(changes, (x: any) => x.id === beat?.id);
  if (beatChange) {
    _.assign(cloneBeats, beatChange);
    return cloneBeats;
  }
  const bulkBeatChanges = findInBulkChanges(bulkChanges, beat);
  if (bulkBeatChanges && bulkBeatChanges?.isReviewed !== beat?.isReviewed) {
    const findReset = _.find(resetBulkChange, (x: any) => x.id === beat?.id);
    if (findReset) return { ...beat };
    return { ...beat, isReviewed: bulkBeatChanges?.isReviewed };
  }
  return { ...beat };
};

/**
 * Marks or unmarks reviewed state across every heat map cell belonging to the selected template. Iterates the template's rrHeatMapCells, adds, updates or removes a bulk review entry per cell when the requested state differs from the cell's current reviewed count, and clears matching reset and per-beat review-change entries.
 *
 * @param input - Object carrying selectedTemplate, the cloneBulkReview, cloneResetList and cloneReviewBeatChanges arrays mutated in place, isReviewed and activeButton.
 * @returns Nothing.
 */
export const handleMarkReviewByTemplateOnly = (input: any = {}) => {
  const {
    selectedTemplate,
    cloneBulkReview,
    cloneResetList,
    cloneReviewBeatChanges,
    isReviewed,
    activeButton,
  } = input;
  _.forEach(selectedTemplate?.rrHeatMapCells, (cell: any) => {
    const existtedItem = _.find(
      cloneBulkReview,
      (item: any) => item.originalTemplateId === selectedTemplate.id && item.cellId === cell.id,
    );
    const isNotChange =
      (cell.reviewed === 0 && !isReviewed) || (cell.reviewed === cell.total && isReviewed);
    if (existtedItem) {
      if (isNotChange) {
        _.remove(
          cloneBulkReview,
          (x: any) => x.originalTemplateId === selectedTemplate.id && x.cellId === cell.id,
        );
      } else {
        existtedItem.isReviewed = isReviewed;
      }
    } else if (!isNotChange) {
      cloneBulkReview.push({
        isReviewed,
        originalTemplateId: selectedTemplate.id,
        originalReviewedCount: cell.reviewed || 0,
        totalCount: cell.total || 0,
        cellId: cell.id,
        type: activeButton,
      });
    }
    _.remove(
      cloneResetList,
      (item: any) => item.originalTemplateId === selectedTemplate.id && item.cellId === cell.id,
    );
    _.remove(
      cloneReviewBeatChanges,
      (item: any) => item.originalTemplateId === selectedTemplate.id && item.cellId === cell.id,
    );
  });
};

/**
 * Marks or unmarks every beat in the selected heat map cells as reviewed, ignoring template selection. Walks each cell and its templates, adding, updating or dropping bulk review entries depending on whether the cell already sits at the requested reviewed state, and clears any per-beat reset or review-change entries for the same cell so the bulk entry wins.
 *
 * @param input - Object carrying currentListCells, the cloneBulkReview, cloneResetList and cloneReviewBeatChanges arrays mutated in place, the target isReviewed flag and activeButton.
 * @returns Nothing.
 */
export const handleMarkReviewByCellOnly = (input: any = {}) => {
  const {
    currentListCells,
    cloneBulkReview,
    cloneResetList,
    cloneReviewBeatChanges,
    isReviewed,
    activeButton,
  } = input;
  _.forEach(currentListCells, (cell: any) => {
    if (!_.isEmpty(cell?.templates)) {
      _.forEach(cell?.templates, (template: any) => {
        const existtedItem = _.find(
          cloneBulkReview,
          (item: any) => item.originalTemplateId === template.id && item.cellId === cell.id,
        );
        const isNotChange =
          (template.reviewed === 0 && !isReviewed) ||
          (template.reviewed === template.total && isReviewed);
        if (existtedItem) {
          if (isNotChange) {
            _.remove(
              cloneBulkReview,
              (x: any) => x.originalTemplateId === template.id && x.cellId === cell.id,
            );
          } else {
            existtedItem.isReviewed = isReviewed;
          }
        } else if (!isNotChange) {
          cloneBulkReview.push({
            isReviewed,
            originalTemplateId: template.id,
            originalReviewedCount: template.reviewed || 0,
            totalCount: template.total || 0,
            cellId: cell.id,
            type: activeButton,
          });
        }
        _.remove(
          cloneResetList,
          (item: any) => item.originalTemplateId === template.id && item.cellId === cell.id,
        );
        _.remove(
          cloneReviewBeatChanges,
          (item: any) => item.originalTemplateId === template.id && item.cellId === cell.id,
        );
      });
    } else {
      const existtedItem = _.find(cloneBulkReview, (item: any) => item.cellId === cell.id);
      const isNotChange =
        (cell.originalReviewedCount === 0 && !isReviewed) ||
        (cell.originalReviewedCount === cell.value && isReviewed);
      if (existtedItem) {
        if (isNotChange) {
          _.remove(cloneBulkReview, (x: any) => x.cellId === cell.id);
        } else {
          existtedItem.isReviewed = isReviewed;
        }
      } else if (!isNotChange) {
        cloneBulkReview.push({
          isReviewed,
          cellId: cell.id,
          originalReviewedCount: cell.originalReviewedCount || 0,
          totalCount: cell.value || 0,
          type: activeButton,
        });
      }
      _.remove(cloneResetList, (item: any) => item.cellId === cell.id);
      _.remove(cloneReviewBeatChanges, (item: any) => item.cellId === cell.id);
    }
  });
};

/**
 * Marks or unmarks reviewed state only where the selected template intersects the selected heat map cells. For each cell it visits only the template matching selectedTemplate, adds, updates or removes the matching bulk review entry when the state actually changes, and drops any reset or per-beat review-change entries for that template and cell pair.
 *
 * @param input - Object carrying currentListCells, selectedTemplate, the cloneBulkReview, cloneResetList and cloneReviewBeatChanges arrays mutated in place, isReviewed and activeButton.
 * @returns Nothing.
 */
export const handleMarkReviewByTemplateAndCell = (input: any = {}) => {
  const {
    currentListCells,
    cloneBulkReview,
    cloneResetList,
    cloneReviewBeatChanges,
    isReviewed,
    activeButton,
    selectedTemplate,
  } = input;
  _.forEach(currentListCells, (cell: any) => {
    _.forEach(cell?.templates, (template: any) => {
      if (template.id === selectedTemplate.id) {
        const existtedItem = _.find(
          cloneBulkReview,
          (item: any) => item.originalTemplateId === template.id && item.cellId === cell.id,
        );
        const isNotChange =
          (template.reviewed === 0 && !isReviewed) ||
          (template.reviewed === template.total && isReviewed);
        if (existtedItem) {
          if (isNotChange) {
            _.remove(
              cloneBulkReview,
              (x: any) => x.originalTemplateId === template.id && x.cellId === cell.id,
            );
          } else {
            existtedItem.isReviewed = isReviewed;
          }
        } else if (!isNotChange) {
          cloneBulkReview.push({
            isReviewed,
            originalTemplateId: template.id,
            originalReviewedCount: template.reviewed || 0,
            totalCount: template.total || 0,
            cellId: cell.id,
            type: activeButton,
          });
        }
        _.remove(
          cloneResetList,
          (item: any) => item.originalTemplateId === template.id && item.cellId === cell.id,
        );
        _.remove(
          cloneReviewBeatChanges,
          (item: any) => item.originalTemplateId === template.id && item.cellId === cell.id,
        );
      }
    });
  });
};

/**
 * Indexes the heat map chart data by cell id so template membership can be looked up per cell. Walks every series row, keeps entries that have both an id and a value, and assigns each cell id the list of its template ids.
 *
 * @param data - Heat map chart data grouped by beat type series.
 * @returns An object mapping each heat map cell id to its array of template ids.
 */
const mapHeatmapTemplates = (data: any = {}) => {
  const object: any = {};
  _.forEach(data, (item: any) => {
    const filterItem = _.filter(item, (x: any) => x.id && x.value);
    if (!_.isEmpty(filterItem)) {
      _.forEach(filterItem, (i: any) => {
        object[i.id] = i.templates;
        _.assign(object, { [i.id]: _.map(i.templates, (x: any) => x.id) });
      });
    }
  });
  return object;
};

/**
 * Collapses review groups covering the same template id set into a single group. Keys a map by the joined templateIds, concatenating rrHeatMapIds and de-duplicating templateIds across the entries that share a key.
 *
 * @param arr - Review group entries to merge.
 * @returns The merged review groups, or undefined when the input is empty.
 */
const mergeByTemplateIdsOnly = (arr: any[]) => {
  if (_.isEmpty(arr)) return undefined;
  const map = new Map();
  arr.forEach((item: any) => {
    // Use templateIds or empty array for grouping
    const templateKey = item.templateIds ? item.templateIds.join(',') : '';
    const key = `template-${templateKey}`;

    if (!map.has(key)) {
      map.set(key, {
        rrHeatMapIds: [...item.rrHeatMapIds],
        ...(item.templateIds ? { templateIds: [...item.templateIds] } : {}),
      });
    } else {
      const existing = map.get(key);
      existing.rrHeatMapIds.push(...item.rrHeatMapIds);
      // merge templateIds if both have them
      if (item.templateIds) {
        existing.templateIds = Array.from(new Set([...existing.templateIds, ...item.templateIds]));
      }
    }
  });
  return Array.from(map.values());
};

/**
 * Builds the GraphQL payload marking Holter beats reviewed or unreviewed. Splits bulk entries into reviewed and unreviewed groups per heat map cell, omitting templateIds when every template in the cell is covered, then merges the groups and collects individual beat ids from the per-beat changes and from reset entries by their original reviewed flag.
 *
 * @param info - Object carrying bulkReviewedBeats, resetBulkReviewedBeats, reviewedBeatsChanges and dataChartHeatMap.
 * @returns An object with isUpdatedReview false when nothing changed, otherwise isUpdatedReview true plus an input section holding the reviewed and unreviewed groups and the beat id lists.
 */
export const formatMutationReviewHolterBeatsInput = (info: any) => {
  const { bulkReviewedBeats, resetBulkReviewedBeats, reviewedBeatsChanges, dataChartHeatMap } =
    info || {};

  if (_.isEmpty(bulkReviewedBeats) && _.isEmpty(reviewedBeatsChanges)) {
    return {
      isUpdatedReview: false,
    };
  }
  const mapInitData = mapHeatmapTemplates(dataChartHeatMap);
  const unReviewedGroup: any[] = [];
  const reviewedGroup: any[] = [];
  const reviewedBeats: any[] = [];
  const unReviewedBeats: any[] = [];
  const groupedReviewedBeats = _.groupBy(
    _.filter(bulkReviewedBeats, (item: any) => item.isReviewed),
    'cellId',
  );
  const groupedUnreviewedBeats = _.groupBy(
    _.filter(bulkReviewedBeats, (item: any) => !item.isReviewed),
    'cellId',
  );
  _.forEach(groupedReviewedBeats, (group: any, cellId: any) => {
    const templateIds = _.compact(_.map(group, (x: any) => x.originalTemplateId));
    const existTemplates = mapInitData[cellId] || [];
    const isAllTemplate = _.difference(existTemplates, templateIds);
    if (!_.isEmpty(isAllTemplate)) {
      reviewedGroup.push({
        rrHeatMapIds: [parseInt(cellId, 10)],
        templateIds: _.sortBy(templateIds),
      });
    } else {
      // all templates reviewed => dont need send templateIds
      reviewedGroup.push({
        rrHeatMapIds: [parseInt(cellId, 10)],
      });
    }
  });
  _.forEach(groupedUnreviewedBeats, (group: any, cellId: any) => {
    const templateIds = _.compact(_.map(group, (x: any) => x.originalTemplateId));
    const existTemplates = mapInitData[cellId] || [];
    const isAllTemplate = _.difference(existTemplates, templateIds);
    if (!_.isEmpty(isAllTemplate)) {
      unReviewedGroup.push({
        rrHeatMapIds: [parseInt(cellId, 10)],
        templateIds: _.sortBy(templateIds),
      });
    } else {
      // all templates unreviewed => dont need send templateIds
      unReviewedGroup.push({
        rrHeatMapIds: [parseInt(cellId, 10)],
      });
    }
  });
  _.forEach(reviewedBeatsChanges, (change: any) => {
    if (change.isReviewed) {
      reviewedBeats.push(change.id);
    } else {
      unReviewedBeats.push(change.id);
    }
  });
  _.forEach(resetBulkReviewedBeats, (change: any) => {
    if (change.originalReviewed) {
      reviewedBeats.push(change.id);
    } else {
      unReviewedBeats.push(change.id);
    }
  });
  const reviewedGroupMerge = mergeByTemplateIdsOnly(reviewedGroup);
  const unReviewedGroupMerge = mergeByTemplateIdsOnly(unReviewedGroup);
  return {
    isUpdatedReview: true,
    input: {
      unReviewedGroup: unReviewedGroupMerge,
      reviewedGroup: reviewedGroupMerge,
      reviewedBeats: !_.isEmpty(reviewedBeats) ? _.uniq(reviewedBeats) : undefined,
      unReviewedBeats: !_.isEmpty(unReviewedBeats) ? _.uniq(unReviewedBeats) : undefined,
    },
  };
};

/**
 * Resolves the beat type behind a beats-tab toolbar button. Looks the button up in a fixed map where both Q variants collapse to Q, falling back to the button value itself when it is not listed.
 *
 * @param activeButton - Identifier of the currently active beat toolbar button.
 * @returns The beat type the button acts on.
 */
const getTypeFromActiveButton = (activeButton: any) => {
  const mapType: any = {
    qToQ: 'Q',
    qToNSV: 'Q',
    V: 'V',
    S: 'S',
    N: 'N',
  };
  return mapType[activeButton] || activeButton;
};

/**
 * Applies a bulk beat-type change over the current heat map selection and returns fresh copies of every pending-change list. Clones the beat, swap and reset lists, branches on whether a template only, cells only, or both are selected, adds or drops bulk entries when the target type differs from the active type, and purges the per-beat entries the bulk entry supersedes.
 *
 * @param input - Object carrying activeButton, the target bulkAction type, the current bulk, reset and per-beat change lists for beats and swaps, listSelectedCells and selectedTemplate.
 * @returns An object holding the cloned bulk, reset and per-beat change lists for beats and swaps, or an empty object when neither cells nor a template are selected.
 */
export const handleUpdateBulkAction = (input: any) => {
  const {
    activeButton,
    bulkAction,

    bulkBeatsChange,
    beatChanges,
    resetBulkBeatsChanges,

    bulkSwapChanges,
    resetBulkSwapChanges,
    swapBeatChanges,

    listSelectedCells,
    selectedTemplate,
  } = input;

  if (_.isEmpty(listSelectedCells) && _.isEmpty(selectedTemplate)) {
    return {};
  }
  const currentType = getTypeFromActiveButton(activeButton);
  const isNotChange = currentType === bulkAction;

  const cloneBulkBeatChange = _.cloneDeep(bulkBeatsChange || []);
  const cloneResetBeats = _.cloneDeep(resetBulkBeatsChanges || []);
  const cloneBeatChanges = _.cloneDeep(beatChanges || []);

  const cloneResetSwapList = _.cloneDeep(resetBulkSwapChanges || []);
  const cloneSwapBeatChanges = _.cloneDeep(swapBeatChanges || []);
  const cloneBulkSwapChanges = _.cloneDeep(bulkSwapChanges || []);

  if (_.isEmpty(listSelectedCells)) {
    if (!_.isEmpty(selectedTemplate)) {
      _.forEach(selectedTemplate?.rrHeatMapCells, (cell: any) => {
        const existtedItem = _.find(
          cloneBulkBeatChange,
          (item: any) => item.originalTemplateId === selectedTemplate.id && item.cellId === cell.id,
        );

        if (existtedItem) {
          if (isNotChange) {
            _.remove(
              cloneBulkBeatChange,
              (x: any) => x.originalTemplateId === selectedTemplate.id && x.cellId === cell.id,
            );
          } else {
            existtedItem.type = bulkAction;
          }
        } else if (!isNotChange) {
          cloneBulkBeatChange.push({
            originalTemplateId: selectedTemplate.id,
            cellId: cell.id,
            originalType: currentType,
            type: bulkAction,
          });
        }
        _.remove(
          cloneResetBeats,
          (item: any) => item.originalTemplateId === selectedTemplate.id && item.cellId === cell.id,
        );
        _.remove(
          cloneBeatChanges,
          (item: any) => item.originalTemplateId === selectedTemplate.id && item.cellId === cell.id,
        );
        _.remove(
          cloneResetSwapList,
          (item: any) => item.originalTemplateId === selectedTemplate.id && item.cellId === cell.id,
        );
        _.remove(
          cloneSwapBeatChanges,
          (item: any) => item.originalTemplateId === selectedTemplate.id && item.cellId === cell.id,
        );
        _.remove(
          cloneBulkSwapChanges,
          (item: any) => item.originalTemplateId === selectedTemplate.id && item.cellId === cell.id,
        );
      });
    }
  } else if (_.isEmpty(selectedTemplate)) {
    _.forEach(listSelectedCells, (cell: any) => {
      if (!_.isEmpty(cell?.templates)) {
        _.forEach(cell?.templates, (template: any) => {
          const existtedItem = _.find(
            cloneBulkBeatChange,
            (item: any) => item.originalTemplateId === template.id && item.cellId === cell.id,
          );
          if (existtedItem) {
            if (isNotChange) {
              _.remove(
                cloneBulkBeatChange,
                (x: any) => x.originalTemplateId === template.id && x.cellId === cell.id,
              );
            } else {
              existtedItem.type = bulkAction;
            }
          } else if (!isNotChange) {
            cloneBulkBeatChange.push({
              originalTemplateId: template.id,
              cellId: cell.id,
              originalType: currentType,
              type: bulkAction,
            });
          }
          _.remove(
            cloneResetBeats,
            (item: any) => item.originalTemplateId === template.id && item.cellId === cell.id,
          );
          _.remove(
            cloneBeatChanges,
            (item: any) => item.originalTemplateId === template.id && item.cellId === cell.id,
          );
          _.remove(
            cloneResetSwapList,
            (item: any) => item.originalTemplateId === template.id && item.cellId === cell.id,
          );
          _.remove(
            cloneSwapBeatChanges,
            (item: any) => item.originalTemplateId === template.id && item.cellId === cell.id,
          );
          _.remove(
            cloneBulkSwapChanges,
            (item: any) => item.originalTemplateId === template.id && item.cellId === cell.id,
          );
        });
      } else {
        const existtedItem = _.find(cloneBulkBeatChange, (item: any) => item.cellId === cell.id);
        if (existtedItem) {
          if (isNotChange) {
            _.remove(cloneBulkBeatChange, (x: any) => x.cellId === cell.id);
          } else {
            existtedItem.type = bulkAction;
          }
        } else if (!isNotChange) {
          cloneBulkBeatChange.push({
            cellId: cell.id,
            originalType: currentType,
            type: bulkAction,
          });
        }
        _.remove(cloneResetBeats, (item: any) => item.cellId === cell.id);
        _.remove(cloneBeatChanges, (item: any) => item.cellId === cell.id);
        _.remove(cloneResetSwapList, (item: any) => item.cellId === cell.id);
        _.remove(cloneSwapBeatChanges, (item: any) => item.cellId === cell.id);
        _.remove(cloneBulkSwapChanges, (item: any) => item.cellId === cell.id);
      }
    });
  } else {
    _.forEach(listSelectedCells, (cell: any) => {
      _.forEach(cell?.templates, (template: any) => {
        if (template.id === selectedTemplate.id) {
          const existtedItem = _.find(
            cloneBulkBeatChange,
            (item: any) => item.originalTemplateId === template.id && item.cellId === cell.id,
          );
          if (existtedItem) {
            if (isNotChange) {
              _.remove(
                cloneBulkBeatChange,
                (x: any) => x.originalTemplateId === template.id && x.cellId === cell.id,
              );
            } else {
              existtedItem.type = bulkAction;
            }
          } else if (!isNotChange) {
            cloneBulkBeatChange.push({
              originalTemplateId: template.id,
              cellId: cell.id,
              originalType: currentType,
              type: bulkAction,
            });
          }
          _.remove(
            cloneResetBeats,
            (item: any) => item.originalTemplateId === template.id && item.cellId === cell.id,
          );
          _.remove(
            cloneBeatChanges,
            (item: any) => item.originalTemplateId === template.id && item.cellId === cell.id,
          );
          _.remove(
            cloneResetSwapList,
            (item: any) => item.originalTemplateId === template.id && item.cellId === cell.id,
          );
          _.remove(
            cloneSwapBeatChanges,
            (item: any) => item.originalTemplateId === template.id && item.cellId === cell.id,
          );
          _.remove(
            cloneBulkSwapChanges,
            (item: any) => item.originalTemplateId === template.id && item.cellId === cell.id,
          );
        }
      });
    });
  }
  return {
    cloneBulkBeatChange,
    cloneResetBeats,
    cloneBeatChanges,
    cloneResetSwapList,
    cloneSwapBeatChanges,
    cloneBulkSwapChanges,
  };
};

/**
 * Maps an internal beat type onto the short label the beats mutation payload expects. Turns the deleted type into 'Del' and the Q type into 'Others', passing any other type through untouched.
 *
 * @param type - Internal beat type enum value to translate.
 * @returns The payload-facing beat type label.
 */
const formatType = (type: any) =>
  type === TYPE_BEAT_ENUM.DELETED ? 'Del' : type === TYPE_BEAT_ENUM.Q ? 'Others' : type;

/**
 * Collapses beat-type groups sharing the same target type and template id set into a single group. Keys a map by type plus the joined templateIds, concatenating rrHeatMapIds and de-duplicating templateIds as matching entries arrive.
 *
 * @param arr - Beat-type group entries to merge.
 * @returns The merged beat-type groups, or undefined when the input is empty.
 */
const mergeByTypeAndTemplate = (arr: any[]) => {
  if (_.isEmpty(arr)) return undefined;
  const map = new Map();

  arr.forEach((item: any) => {
    // Use templateIds or empty array for grouping
    const templateKey = item.templateIds ? item.templateIds.join(',') : '';
    const key = `${item.type}-${templateKey}`;

    if (!map.has(key)) {
      map.set(key, {
        type: item.type,
        rrHeatMapIds: [...item.rrHeatMapIds],
        ...(item.templateIds ? { templateIds: [...item.templateIds] } : {}),
      });
    } else {
      const existing = map.get(key);
      existing.rrHeatMapIds.push(...item.rrHeatMapIds);
      // merge templateIds if both have them
      if (item.templateIds) {
        existing.templateIds = Array.from(new Set([...existing.templateIds, ...item.templateIds]));
      }
    }
  });

  return Array.from(map.values());
};

/**
 * Collapses swap groups that target the same template and cover the same template id set into a single group. Keys a map by targetTemplateId plus the joined templateIds, concatenating rrHeatMapIds and de-duplicating templateIds and exceptBeats as matching entries arrive.
 *
 * @param arr - Swap group entries to merge.
 * @returns The merged swap groups, or undefined when the input is empty.
 */
const mergeByTargetTemplateIdAndTemplate = (arr: any[]) => {
  if (_.isEmpty(arr)) return undefined;
  const map = new Map();

  arr.forEach((item: any) => {
    // Use templateIds or empty array for grouping
    const templateKey = item.templateIds ? item.templateIds.join(',') : '';
    const key = `${item.targetTemplateId}-${templateKey}`;

    if (!map.has(key)) {
      map.set(key, {
        targetTemplateId: item.targetTemplateId,
        rrHeatMapIds: [...item.rrHeatMapIds],
        ...(item.templateIds ? { templateIds: [...item.templateIds] } : {}),
        ...(item.exceptBeats ? { exceptBeats: [...item.exceptBeats] } : {}),
      });
    } else {
      const existing = map.get(key);
      existing.rrHeatMapIds.push(...item.rrHeatMapIds);
      // merge templateIds if both have them
      if (item.templateIds) {
        existing.templateIds = Array.from(new Set([...existing.templateIds, ...item.templateIds]));
      }
      if (item.exceptBeats) {
        existing.exceptBeats = Array.from(
          new Set([...(existing.exceptBeats || []), ...item.exceptBeats]),
        );
      }
    }
  });

  return Array.from(map.values());
};

/**
 * Builds the GraphQL payload that changes beat types from the pending bulk and per-beat edits. Groups bulk changes by heat map cell, sending templateIds only when part of the cell's templates are affected, then appends reset beats with their original type and changed beats with an isNew flag derived from a missing oldType, merging groups by type and de-duplicating beats by time.
 *
 * @param input - Object carrying beatChanges, bulkBeatsChange, resetBulkBeatsChanges and dataChartHeatMap.
 * @returns An object with isUpdated false when nothing changed, otherwise isUpdated true plus an input section holding the merged groups and beats.
 */
export const formatMutationBeatsInput = (input: any) => {
  const { beatChanges, bulkBeatsChange, resetBulkBeatsChanges, dataChartHeatMap } = input;
  if (_.isEmpty(beatChanges) && _.isEmpty(bulkBeatsChange)) {
    return {
      isUpdated: false,
    };
  }

  const mapInitData = mapHeatmapTemplates(dataChartHeatMap);
  const groups: any[] = [];
  const beats: any[] = [];
  const groupedBeatsChange = _.groupBy(bulkBeatsChange, 'cellId');
  _.forEach(groupedBeatsChange, (group: any, cellId: any) => {
    const templateIds = _.compact(_.map(group, (x: any) => x.originalTemplateId));
    const existTemplates = mapInitData[cellId] || [];
    const isAllTemplate = _.difference(existTemplates, templateIds);
    const typeGroup = getTypeFromActiveButton(group[0].type);
    if (!_.isEmpty(isAllTemplate)) {
      groups.push({
        rrHeatMapIds: [parseInt(cellId, 10)],
        templateIds: _.sortBy(templateIds),
        type: formatType(typeGroup),
      });
    } else {
      // all templates reviewed => dont need send templateIds
      groups.push({
        rrHeatMapIds: [parseInt(cellId, 10)],
        type: formatType(typeGroup),
      });
    }
  });

  _.forEach(resetBulkBeatsChanges, (reset: any) => {
    beats.push({
      time: reset.id,
      type: formatType(reset.originalType),
      isNew: false,
    });
  });

  _.forEach(beatChanges, (change: any) => {
    beats.push({
      time: change.id,
      type: formatType(change.type),
      isNew: change.oldType === undefined,
    });
  });
  const mergedGroups = mergeByTypeAndTemplate(groups);
  return {
    isUpdated: true,
    input: {
      groups: mergedGroups,
      beats: _.isEmpty(beats) ? undefined : _.uniqBy(beats, 'time'),
    },
  };
};

/**
 * Builds the GraphQL payload that moves beats between morphology templates and updates which templates are included in the report. Groups bulk swaps by target template and cell, sending templateIds only when some templates in the cell are untouched and listing reset beats as exceptBeats, appends per-beat swaps, merges the groups, and derives the selected and deselected template lists from include-in-report flips.
 *
 * @param input - Object carrying swapBeatChanges, templateChanges, bulkSwapBeats, resetSwapBeats and dataChartHeatMap.
 * @returns An object with isUpdated false when nothing changed, otherwise isUpdated true plus the morphologyBeatTemplateSwap and includeMorphologyToReport payload sections.
 */
export const formatMutationBeatsSwapInput = (input: any) => {
  const { swapBeatChanges, templateChanges, bulkSwapBeats, resetSwapBeats, dataChartHeatMap } =
    input;
  if (_.isEmpty(swapBeatChanges) && _.isEmpty(templateChanges) && _.isEmpty(bulkSwapBeats)) {
    return {
      isUpdated: false,
    };
  }
  const groups: any[] = [];
  const beats: any[] = [];
  if (!_.isEmpty(bulkSwapBeats)) {
    const mapInitData = mapHeatmapTemplates(dataChartHeatMap);
    const groupedTargetTemplateChange = _.groupBy(bulkSwapBeats, 'templateId');
    _.forEach(groupedTargetTemplateChange, (templateGroup: any, templateId: any) => {
      const groupedBeatsChange = _.groupBy(templateGroup, 'cellId');
      _.forEach(groupedBeatsChange, (group: any, cellId: any) => {
        const templateIds = _.compact(_.map(group, (x: any) => x.originalTemplateId));
        const existTemplates = mapInitData[cellId] || [];
        const isAllTemplate = _.difference(existTemplates, templateIds);
        if (!_.isEmpty(isAllTemplate)) {
          const exceptBeats = _.filter(
            resetSwapBeats,
            (x: any) => x.cellId === cellId && _.includes(templateIds, x.templateId),
          );
          groups.push({
            rrHeatMapIds: [parseInt(cellId, 10)],
            templateIds: _.sortBy(templateIds),
            targetTemplateId: parseInt(templateId, 10),
            exceptBeats: _.isEmpty(exceptBeats) ? undefined : _.map(exceptBeats, (x: any) => x.id),
          });
        } else {
          // all templates reviewed => dont need send templateIds
          const exceptBeats = _.filter(
            resetSwapBeats,
            (x: any) => x.cellId === parseInt(cellId, 10),
          );
          groups.push({
            rrHeatMapIds: [parseInt(cellId, 10)],
            exceptBeats: _.isEmpty(exceptBeats) ? undefined : _.map(exceptBeats, (x: any) => x.id),
            targetTemplateId: parseInt(templateId, 10),
          });
        }
      });
    });
  }
  if (!_.isEmpty(swapBeatChanges)) {
    _.forEach(swapBeatChanges, (change: any) => {
      beats.push({
        time: change.id,
        templateId: change.templateId,
      });
    });
  }

  const filterUpdateIncludeReport = _.filter(
    templateChanges,
    (x: any) => x.originalData?.isIncludedToReport !== x.isIncludedToReport,
  );
  const selectedTemplates = _.map(
    _.filter(filterUpdateIncludeReport, (x: any) => x.isIncludedToReport),
    (x: any) => x.id,
  );
  const deSelectedTemplates = _.map(
    _.filter(filterUpdateIncludeReport, (x: any) => !x.isIncludedToReport),
    (x: any) => x.id,
  );
  if (_.isEmpty(groups) && _.isEmpty(beats) && _.isEmpty(filterUpdateIncludeReport)) {
    return {
      isUpdated: false,
    };
  }
  const mergedGroups = mergeByTargetTemplateIdAndTemplate(groups);
  return {
    isUpdated: true,
    morphologyBeatTemplateSwap:
      _.isEmpty(groups) && _.isEmpty(beats)
        ? undefined
        : {
            groups: mergedGroups,
            beats: _.isEmpty(beats) ? undefined : beats,
          },
    includeMorphologyToReport: !_.isEmpty(filterUpdateIncludeReport)
      ? {
          selectedTemplates: !_.isEmpty(selectedTemplates) ? selectedTemplates : undefined,
          deSelectedTemplates: !_.isEmpty(deSelectedTemplates) ? deSelectedTemplates : undefined,
        }
      : undefined,
  };
};
